import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, NgZone } from '@angular/core';
import { Toast, ToastPackage, ToastrService } from 'ngx-toastr';

/**
 * ARIA semantics carried from the facade's ToastRequest through the toast
 * payload (contracts/notification-conventions.md): info/success/warning are
 * polite status updates; errors are assertive alerts. The library default
 * component hard-codes role="alert" on every message, so the facade's values
 * would otherwise never reach the rendered markup.
 */
export interface AithToastPayload {
  readonly role: 'status' | 'alert';
  readonly ariaLive: 'polite' | 'assertive';
}

/**
 * The house toast component (T037 follow-up): extends the library's Toast so
 * all dismissal/timeout/stick-around behavior is inherited, but renders the
 * live-region semantics the facade computed. The flyInOut trigger replicates
 * the library's — animations are not inherited through component extension —
 * and its timing still comes from the per-toast `easeTime` config, which the
 * outlet sets to 0 when the user requests reduced motion (R-012).
 */
// The library instantiates this component dynamically as the configured
// toastComponent (like its own attribute-selected default), so the element-
// selector rule does not apply here.
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-aith-toast]',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (options.closeButton) {
      <button
        type="button"
        class="toast-close-button"
        aria-label="Dismiss notification"
        (click)="remove()"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    }
    @if (message) {
      <div
        class="toast-message"
        [class]="options.messageClass"
        [attr.role]="role"
        [attr.aria-live]="ariaLive"
        [attr.aria-label]="message"
      >
        {{ message }}
      </div>
    }
    @if (options.progressBar) {
      <div>
        <div class="toast-progress" [style.width]="width() + '%'"></div>
      </div>
    }
  `,
  animations: [
    trigger('flyInOut', [
      state('inactive', style({ opacity: 0 })),
      state('active', style({ opacity: 1 })),
      state('removed', style({ opacity: 0 })),
      transition('inactive => active', animate('{{ easeTime }}ms {{ easing }}')),
      transition('active => removed', animate('{{ easeTime }}ms {{ easing }}')),
    ]),
  ],
})
export class AithToastComponent extends Toast {
  constructor(
    toastrService: ToastrService,
    toastPackage: ToastPackage,
    ngZone: NgZone,
  ) {
    super(toastrService, toastPackage, ngZone);
  }

  protected get role(): 'status' | 'alert' {
    return this.payload?.role ?? 'status';
  }

  protected get ariaLive(): 'polite' | 'assertive' {
    return this.payload?.ariaLive ?? 'polite';
  }

  private get payload(): AithToastPayload | undefined {
    return this.toastPackage.config.payload as AithToastPayload | undefined;
  }
}
